import { useParams } from '@tanstack/react-router';
import { cloneDeep } from 'lodash';
import { useState } from 'react';
import { useAsyncRetry } from 'react-use';
import { useSnapshot } from 'valtio';
import { addSingleFile as apiAddSingleFile } from '~/api/files';
import { getCivilSearchMessages as apiGetMessages, newCivilSearchMessage as apiNewMessage } from '~/api/projects2';
import { constructFile } from '~/common/miscUtils';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Prefs } from '~/common/prefs';
import { FileCategory } from '~/common/types';
import EmailLink from '~/components2/EmailLink';
import MessageForm from '~/components2/Messages/MessageForm';
import { Notify } from '~/components2/Notify';
import rootStore from '~/store';
import type { ProjectDetailsParams } from '../../..';
import store from '../../store';
import Drawer, { DrawerContainer, DrawerContent, DrawerFooter } from '../Drawer';
import MessageList from './MessageList';

export default function MessagesComponent() {
  const [isProcessing, setIsProcessing] = useState(false);
  const { projectId } = useParams({ strict: false }) as ProjectDetailsParams;
  const { me } = useSnapshot(rootStore) as typeof rootStore;

  const messages = useAsyncRetry(async () => {
    const res = await apiGetMessages(projectId);
    const forward = res?.sort((a, b) => {
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });
    const backward = cloneDeep(forward).reverse();
    const id = backward.find((m) => m.createdBy.id !== me?.id)?.id;
    if (id) {
      store.setLastViewed(Prefs.LAST_VIEWED_MESSAGE_ID, 'message', id);
    }
    return forward;
  }, []);

  const onSubmit = async (current: string, files: File[]) => {
    if (!current && !files.length) return;

    try {
      setIsProcessing(true);

      let fileResponses: any = [];

      if (files.length) {
        const uploads = files.map(async (file) => {
          const f = constructFile(file, {
            category: FileCategory.SupportingFile,
          });
          return await apiAddSingleFile(projectId, f);
        });

        fileResponses = await Promise.all(uploads);
      }

      const message = {
        message: current,
        files: fileResponses,
      };

      await apiNewMessage(projectId, message);

      capturePosthogEvent(POSTHOG_EVENTS.civilsearchMessageAdded, {
        project_id: projectId,
      });

      messages.retry();
    } catch (e) {
      console.error('Error adding message: ', e);
      Notify.error({ message: 'Error adding message.' });
    } finally {
      setIsProcessing(false);
    }
  };

  if (messages.error) {
    return (
      <Drawer
        title="Project Channel"
        tooltip="Communicate directly with the CivilGrid team regarding this specific project. Your messages will be seen across your project team and are specific to just this project."
      >
        <div>
          En error occurred while fetching messages, please <EmailLink>contact support</EmailLink>.
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer
      title="Project Channel"
      tooltip="Communicate directly with the CivilGrid team regarding this specific project. Your messages will be seen across your project team and are specific to just this project."
    >
      <DrawerContainer>
        <DrawerContent>
          <MessageList messages={messages.value || []} />
        </DrawerContent>
        <DrawerFooter className="space-x-2">
          <MessageForm onSubmit={onSubmit} isProcessing={isProcessing} />
        </DrawerFooter>
      </DrawerContainer>
    </Drawer>
  );
}
